// MASK FILL — a coverage mask filled WITHOUT a 2D canvas (owner ruling
// 2026-10-25: exact data never passes through a canvas; Brave's canvas
// farbling flips low bits on getImageData). ways2.js's road mask (grass,
// player, net and the HUD threshold it at 0.3 / 0.35) is filled here: the
// polygons a canvas fill drew, the same nonzero rule, the same source-over
// composite (v += c (255 − v)), deterministic on every device. Coverage c of a
// cell = the mean over SUB sub-scanlines of the exact covered length — a
// canvas's anti-aliasing, to within 1 / SUB vertically.
// Gate: scripts/check-ring.mjs exact:no-canvas (the region below) +
// exact:maskfill (unit shapes, area, twins).

const SUB = 4;                  // sub-scanlines per cell row

// EXACT-DATA-BEGIN (scripts/check-ring.mjs exact:no-canvas: no canvas, bitmap or readback in here)
// a[0..n) ascending: insertion for the usual few crossings, a typed sort for many
// (an east-west road has thousands of edges in one row)
function sortN(a, n) {
  if (n > 24) { a.subarray(0, n).sort(); return; }
  for (let i = 1; i < n; i++) {
    const x = a[i];
    let q = i;
    while (q > 0 && a[q - 1] > x) { a[q] = a[q - 1]; q--; }
    a[q] = x;
  }
}
// a fill target: mask = Uint8Array w·h (row 0 = y 0, 0 none .. 255 covered)
export function maskFill(w, h) {
  const mask = new Uint8Array(w * h), acc = new Float32Array(w + 1), cnt = new Int32Array(h + 2);
  let B = null;                  // the per-edge scratch, grown to the largest polygon
  const grow = (n) => ({ n, ex: new Float64Array(2 * n), ey0: new Float64Array(n), ey1: new Float64Array(n), xp: new Float64Array(n),
    xn: new Float64Array(n), edir: new Int8Array(n), eord: new Int32Array(n), act: new Int32Array(n) });
  // poly: flat [x0, y0, x1, y1, …] in cells (x east, y = row), closed implicitly
  const fill = (poly) => {
    const m = poly.length >> 1;
    if (m < 3) return;
    if (!B || B.n < m) B = grow(Math.max(64, 2 * m));
    const { ex, ey0, ey1, xp, xn, edir, eord, act } = B;
    // the non-horizontal edges, top end first: x at y0, dx/dy, winding
    let ne = 0, ymin = Infinity, ymax = -Infinity, xmin = Infinity, xmax = -Infinity;
    for (let i = 0; i < m; i++) {
      const j = (i + 1) % m, xa = poly[2 * i], ya = poly[2 * i + 1], xb = poly[2 * j], yb = poly[2 * j + 1];
      xmin = Math.min(xmin, xa); xmax = Math.max(xmax, xa);
      if (ya === yb) continue;
      const d = ya < yb ? 1 : -1, x0 = d > 0 ? xa : xb, y0 = d > 0 ? ya : yb, y1 = d > 0 ? yb : ya;
      ex[2 * ne] = x0; ex[2 * ne + 1] = ((d > 0 ? xb : xa) - x0) / (y1 - y0);
      ey0[ne] = y0; ey1[ne] = y1; edir[ne] = d;
      ymin = Math.min(ymin, y0); ymax = Math.max(ymax, y1);
      ne++;
    }
    if (!ne || xmax <= 0 || xmin >= w) return;
    const r0 = Math.max(0, Math.floor(ymin)), r1 = Math.min(h - 1, Math.ceil(ymax) - 1);
    if (r1 < r0) return;
    // the edges bucketed by their first row (a counting sort: eord[cnt[r]..cnt[r + 1]))
    const R = r1 - r0 + 1, row = (e) => Math.max(0, Math.floor(ey0[e]) - r0);
    cnt.fill(0, 0, R + 1);
    for (let e = 0; e < ne; e++) { const b = row(e); if (b < R) cnt[b + 1]++; }
    for (let b = 0; b < R; b++) cnt[b + 1] += cnt[b];
    for (let e = 0; e < ne; e++) { const b = row(e); if (b < R) eord[cnt[b]++] = e; }
    let na = 0;
    for (let r = r0, k0 = 0; r <= r1; r++) {
      const k1 = cnt[r - r0];        // after the fill pass: the end of row r's bucket
      while (k0 < k1) act[na++] = eord[k0++];
      let lo = w, hi = -1;           // the cells this row touched
      for (let s = 0; s < SUB; s++) {
        const y = r + (s + 0.5) / SUB;
        // the edges crossing y, by winding: up (+1) and down (−1) x, each sorted
        let np = 0, nn = 0;
        for (let k = 0; k < na; k++) {
          const e = act[k];
          if (ey1[e] <= y) { act[k--] = act[--na]; continue; }
          if (ey0[e] > y) continue;
          const x = ex[2 * e] + (y - ey0[e]) * ex[2 * e + 1];
          if (edir[e] > 0) xp[np++] = x; else xn[nn++] = x;
        }
        if (np > 1) sortN(xp, np);
        if (nn > 1) sortN(xn, nn);
        // the merged walk: nonzero spans -> exact covered length per cell, weight 1 / SUB
        for (let i = 0, j = 0, wn = 0, x0 = 0; i < np || j < nn;) {
          const up = j >= nn || (i < np && xp[i] <= xn[j]), x1 = up ? xp[i++] : xn[j++];
          const was = wn;
          wn += up ? 1 : -1;
          const a = Math.max(0, x0), b = Math.min(w, x1);
          x0 = x1;
          if (!was || b <= a) continue;
          const ia = Math.floor(a), ib = Math.floor(b);
          if (ia < lo) lo = ia;
          if (ib > hi) hi = ib;
          if (ia === ib) { acc[ia] += (b - a) / SUB; continue; }
          acc[ia] += (ia + 1 - a) / SUB;
          for (let q = ia + 1; q < ib; q++) acc[q] += 1 / SUB;
          acc[ib] += (b - ib) / SUB;
        }
      }
      // source-over into the mask; acc cleared for the next row
      const o = r * w;
      for (let i = lo, e = Math.min(hi, w - 1); i <= e; i++) {
        const c = acc[i];
        if (c > 0) { const v = mask[o + i]; mask[o + i] = v + Math.round((c < 1 ? c : 1) * (255 - v)); acc[i] = 0; }
      }
      acc[w] = 0;
    }
  };
  return { mask, fill };
}
// EXACT-DATA-END
